import { useState, useEffect } from 'react';
import {
  CreateUniversalButtonBody,
  UniversalButton
} from '~/types/universalButton';
import CobuConstants from '~/helpers/cobuConstants';
import { toast } from 'react-toastify';
import {
  createUniversalBtn,
  deactivateUniversalBtn,
  getAllActiveUniversalBtns,
  updateUniversalBtn
} from '~/api/universalButton';

interface LoadingConfig {
  flag: boolean;
  comment?: string;
}

const useUniversalButtons = () => {
  const [activeUniversalButtons, setActiveUniversalButtons] = useState<
    UniversalButton[]
  >([]);
  const [loadingConfig, setLoadingConfig] = useState<LoadingConfig>({
    flag: true,
    comment: 'Loading universal buttons...'
  });

  const fetchUniversalButtons = async (refresh: boolean = false) => {
    setLoadingConfig({
      flag: true,
      comment: refresh
        ? CobuConstants.activeUniversalButtons.refreshingActiveUniversalButtons
        : CobuConstants.activeUniversalButtons.fetchingActiveUniversalButtons
    });
    try {
      const response = await getAllActiveUniversalBtns();
      setActiveUniversalButtons(response.data);
      setLoadingConfig({ flag: false });
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error?.message) {
        toast(error?.message);
      }
    }
  };

  useEffect(() => {
    fetchUniversalButtons();
  }, []);

  const createUniversalButton = async (button: CreateUniversalButtonBody) => {
    setLoadingConfig({
      flag: true,
      comment: CobuConstants.activeUniversalButtons.createUniversalButtonLoading
    });
    try {
      await createUniversalBtn(button);
      await fetchUniversalButtons(true);
      toast(CobuConstants.activeUniversalButtons.createUniversalButtonSuccess);
    } catch (error) {
      if (error?.message) {
        toast(error?.message);
      }
    } finally {
      setLoadingConfig({ flag: false });
    }
  };

  const updateUniversalButton = async (button: CreateUniversalButtonBody) => {
    setLoadingConfig({
      flag: true,
      comment: CobuConstants.activeUniversalButtons.editUniversalButtonLoading
    });
    try {
      await updateUniversalBtn(button);
      await fetchUniversalButtons(true);
      toast(CobuConstants.activeUniversalButtons.editUniversalButtonSuccess);
    } catch (error) {
      if (error?.message) {
        toast(error?.message);
      }
    } finally {
      setLoadingConfig({ flag: false });
    }
  };

  const deactivateUniversalButton = async (button: UniversalButton) => {
    setLoadingConfig({
      flag: true,
      comment: CobuConstants.activeUniversalButtons.deactivatingButton
    });
    try {
      await deactivateUniversalBtn(button.uuid);
      setActiveUniversalButtons(
        activeUniversalButtons.filter((b) => b.uuid !== button.uuid)
      );
    } catch (error) {
      if (error?.message) {
        toast(error?.message);
      }
    } finally {
      setLoadingConfig({ flag: false });
    }
  };

  return {
    activeUniversalButtons,
    loadingConfig,
    createUniversalButton,
    updateUniversalButton,
    deactivateUniversalButton
  };
};

export default useUniversalButtons;
